import React from 'react'
import { Formik, FormikHelpers, Form } from 'formik'
import { AdminApi } from '../admin.api'
import { useNavigate } from 'react-router-dom'
import { RoutesEnum } from '../../routes.constants'
import FixedPageTitle from '../components/fixed-page-title.component'
import { createEventSchema } from '../validation.schemas'
import { Column, Container } from '../../shared/shared.styled'
import PrimaryButton from '../../shared/components/button.component'
import { useAlert } from 'react-alert'
import { useModal } from 'react-modal-hook'
import { ConfirmationModal } from '../../shared/components/modals/confirmation.modal'
import { CreateEventRequest } from '../admin.interfaces'
import EventFields from '../components/event-fields.component'
import { EventPhoto } from '../components/event-photo.component'
import { Event as CompanyEvent } from '../../shared/shared.models'
import Loader from 'react-spinners/DotLoader'
import { Typography, Colors } from '../../configs/styled.config'
import styled from 'styled-components'

interface EventPageProps {
    eventId: string
}

export const EventPage = ({ eventId }: EventPageProps) => {
    const navigate = useNavigate()
    const alert = useAlert()

    const [event, setEvent] = React.useState<CompanyEvent>()
    const [isLoading, setIsLoading] = React.useState(true)

    const fetchEvent = React.useCallback(async () => {
        try {
            const res = await AdminApi.methods.getEvent(eventId)
            setEvent(res.data.event)
        } catch (e) {
            alert.error(
                'Houve um erro a ir buscar a informação deste evento. Por favor tente mais tarde'
            )
        } finally {
            setIsLoading(false)
        }
    }, [eventId, alert])

    React.useEffect(() => {
        fetchEvent()
    }, [fetchEvent])

    const [showConfirmationModal, hideConfirmationModal] = useModal(
        () => (
            <ConfirmationModal
                title="Tem a certeza que quer sair"
                description="Vai perder toda a informação que modificou"
                onClick={() => {
                    hideConfirmationModal()
                    window.history.back()
                }}
                onCancel={hideConfirmationModal}
            />
        ),
        []
    )

    const [showDeleteModal, hideDeleteModal] = useModal(
        () => (
            <ConfirmationModal
                title="Tem a certeza que quer eliminar este evento"
                description="Esta ação não pode ser desfeita"
                onClick={async () => {
                    hideDeleteModal()
                    try {
                        await AdminApi.methods.deleteEvent(eventId)
                        navigate(RoutesEnum.EVENTS)
                    } catch (e) {
                        alert.error(
                            'Erro ao eliminar o evento. Por favor tente mais tarde'
                        )
                    }
                }}
                onCancel={hideDeleteModal}
            />
        ),
        [eventId]
    )

    const updateEvent = async (
        values: CreateEventRequest,
        actions: FormikHelpers<CreateEventRequest>
    ) => {
        try {
            actions.setSubmitting(true)
            const res = await AdminApi.methods.updateEvent(eventId, values)
            setEvent(res.data.event)
            navigate(RoutesEnum.EVENTS)
        } catch (e) {
            alert.error(
                'Erro ao atualizar o evento. Por favor tente mais tarde'
            )
        } finally {
            actions.setSubmitting(false)
        }
    }

    const checkIfFormIsDirtyAndClose = (isDirty: boolean) => {
        if (isDirty) {
            showConfirmationModal()
        } else {
            window.history.back()
        }
    }

    const renderButtons = (isSubmitting: boolean, isDirty: boolean) => (
        <>
            <PrimaryButton label="Eliminar" onClick={showDeleteModal} />
            <PrimaryButton
                label="Cancelar"
                onClick={() => checkIfFormIsDirtyAndClose(isDirty)}
            />
            <PrimaryButton
                isLoading={isSubmitting}
                type="submit"
                label="Guardar alterações"
                redTheme={true}
            />
        </>
    )

    if (isLoading) {
        return (
            <Column
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    left: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Loader color={Colors['gold-pmauto']} />
                <LoadingText>A carregar o evento...</LoadingText>
            </Column>
        )
    }

    if (!event) {
        throw new Error('Este evento não existe')
    }

    return (
        <Container>
            <Formik<CreateEventRequest>
                onSubmit={updateEvent}
                initialValues={{
                    name: event.name,
                    startDate: event.startDate,
                    endDate: event.endDate,
                    imageUrl: event.imageUrl || '',
                    active: event.active,
                }}
                validateOnChange={false}
                validationSchema={createEventSchema()}
                enableReinitialize={true}
            >
                {({ isSubmitting, handleSubmit, dirty }) => (
                    <Form
                        translate="yes"
                        onSubmit={isSubmitting ? () => null : handleSubmit}
                    >
                        <FixedPageTitle
                            label={event.name}
                            rightButtons={renderButtons(isSubmitting, dirty)}
                        />

                        <EventFields isEditing={true} />
                        <EventPhoto event={event} />
                    </Form>
                )}
            </Formik>
        </Container>
    )
}

const LoadingText = styled('span')`
    margin-top: 24px;
    ${Typography['Body_Text_#1_Regular_Center']}
`
